/*
 * Edit alert Form
 * Add and Edit alerts
 *
 */

import { TFunction } from 'i18next';
import includes from 'lodash/includes';
import * as React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FieldConfig, FormArray, FormGenerator, FormGroup, Validators } from 'react-reactive-form';
import { toastr } from 'react-redux-toastr';
import styled from 'styled-components';
import {
    clearSelectedInstallBasePartID,
    saveInstallBasePart,
    setSelectedInstallBasePartID
} from '../../actions/partsActions';
import { constants } from '../../constants/constants';
import {
    Ihistory,
    IinstallBasePart,
    IinstallBasePartPopulated,
    IjobPart,
    Ioption,
    IpartStock,
    IworkOrderPart
} from '../../models';
import { IpartFormValues } from '../../modelsForms';
import { FormUtil } from '../common/FormUtil';
import { addQuery } from '../common/OtherUtils';
import { suggestedPartsQueryParamsEnum } from './SuggestedParts';

interface Iprops {
    partOptions: Ioption[];
    clearSelectedInstallBasePartID: typeof clearSelectedInstallBasePartID;
    colorButton?: string;
    disabled?: boolean;
    formValues: IpartFormValues;
    installBaseID: string;
    loading: boolean;
    selectedJobID: string;
    selectedInstallBasePartPopulated: IinstallBasePartPopulated;
    setSelectedInstallBasePartID: typeof setSelectedInstallBasePartID;
    standardOptions: Ioption[];
    t: TFunction;
    tableParts: IinstallBasePartPopulated[];
    updateFormValue: (formValue: { [key: string]: any }) => any;
    workOrderID?: string;
    jobParts: { [key: string]: IjobPart };
    partStocks: { [key: string]: IpartStock };
    workOrderParts: { [key: string]: IworkOrderPart };
    saveInstallBasePart: typeof saveInstallBasePart;
    history: Ihistory;
    isUserUSA: boolean;
    isMobile: boolean;
    selectedParts: IinstallBasePartPopulated[];
}

const FormSection = styled.div`
    .beacon-form {
        .col-xs-2 {
            width: auto;
        }
    }
`;

const PartForm: React.FC<Iprops> = (props: Iprops) => {
    const [formGroup, setFormGroup] = React.useState<FormGroup | any>();
    let formRef: FormGroup | any;

    /*
     * selectedItemToFormValues - take the selectedItem and convert it to formValues
     */
    const selectedItemToFormValues = (installBasePartPopulated: IinstallBasePartPopulated): IpartFormValues => {
        const {
            id,
            part,
            jobPartQuantity,
            partStockQuantity,
            directPartQuantity,
            workOrderPartQuantity
        } = installBasePartPopulated;
        const finalQuantity = jobPartQuantity || partStockQuantity || workOrderPartQuantity || directPartQuantity || 1;
        return {
            id,
            description: part.number ? `${part.number} - ${part.description}` : '',
            quantity: finalQuantity
        };
    };

    const initValue = () => {
        const { selectedInstallBasePartPopulated } = props;

        const {
            id,
            part: { number, description },
            directPartQuantity,
            workOrderPartQuantity,
            partStockQuantity
        } = selectedInstallBasePartPopulated;

        const defaultPart = { disabled: false, label: '', value: '' };
        const defaultQuantity = 1;

        const finalQuantity = directPartQuantity || workOrderPartQuantity || partStockQuantity || defaultQuantity;

        return {
            ...formGroup,
            value: {
                parts: id ? { value: id, label: `${number} - ${description}` } : defaultPart,
                quantity: finalQuantity
            }
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        // When the Device or Work Order changes, reset the part form
        setFormGroup(initValue());
        // Clear any selected part when work order changes to ensure clean form state
        if (props.selectedInstallBasePartPopulated.id && props.clearSelectedInstallBasePartID) {
            props.clearSelectedInstallBasePartID();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.installBaseID, props.workOrderID]);

    /*
     * formValuesToSelectedItem - convert the formValues to the shape of the selectedItem
     * when we are creating new, the formValues.parts = a jobPartID or a stockPartID
     * when we are editing, the formValues.parts = the materialID
     */
    const formValuesToSelectedItem = (): IinstallBasePart => {
        const formValues = FormUtil.getValues(formRef.value);
        let quantity = parseFloat(`${formValues.quantity}`);

        // This should really only apply to work order parts
        // When WO Part is selected, and we update the quantity to the WO part's estimated amount, it's not updating in the formValues
        const formQuantity = formRef.get('quantity');
        if (formQuantity && formQuantity.value) {
            // if formGroup has a higher quantity, use that one
            const quantityValue = parseFloat(`${formQuantity.value}`);

            if (quantityValue && quantityValue > quantity) {
                quantity = quantityValue;
            }
        }

        // Ensure quantity is a valid number (fixes issue where NaN/null becomes null in payload)
        if (isNaN(quantity) || quantity === null || quantity === undefined || quantity < 0) {
            quantity = 1;
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { part, ...installBasePart } = props.selectedInstallBasePartPopulated;

        if (installBasePart.id) {
            const isPartStock = installBasePart.partStockID?.length;
            const isJobPart = installBasePart.jobPartID?.length;
            const isDirectPart = installBasePart.directPartID?.length;
            const isWorkOrderPart = installBasePart.workOrderPartID?.length;
            return {
                ...installBasePart,
                jobPartQuantity: isJobPart ? quantity : 0,
                partStockQuantity: isPartStock ? quantity : 0,
                directPartQuantity: isDirectPart ? quantity : 0,
                workOrderPartQuantity: isWorkOrderPart ? quantity : 0
            };
        } else {
            const partStock = props.partStocks[formValues.parts as string];
            const jobPart = props.jobParts[formValues.parts as string];
            const workOrderPart = props.workOrderParts[formValues.parts as string];
            const isDirectPart = !partStock && !jobPart && !workOrderPart;

            // For boot stock parts, ensure quantity is valid (use partStock quantity as fallback if available)
            let finalQuantity = quantity;
            if (partStock && (isNaN(quantity) || quantity === null || quantity === undefined || quantity < 0)) {
                finalQuantity = partStock.quantity > 0 ? partStock.quantity : 1;
            }

            return {
                ...installBasePart,
                jobID: installBasePart.jobID || props.selectedJobID,
                installBaseID: installBasePart.installBaseID || props.installBaseID,
                jobPartID: jobPart ? jobPart.id : null,
                jobPartQuantity: jobPart ? quantity : 0,
                partStockID: partStock ? partStock.id : null,
                partStockQuantity: partStock ? finalQuantity : 0,
                directPartID: isDirectPart ? (formValues.parts as string) : null,
                directPartQuantity: isDirectPart ? quantity : 0,
                workOrderPartID: workOrderPart ? workOrderPart.id : null,
                workOrderPartQuantity: workOrderPart ? quantity : 0
            };
        }
    };

    const getPartTypeID = (installBasePart: IinstallBasePart) => {
        if (installBasePart.jobPartID) {
            return installBasePart.jobPartID;
        } else if (installBasePart.partStockID) {
            return installBasePart.partStockID;
        } else if (installBasePart.workOrderPartID) {
            return installBasePart.workOrderPartID;
        } else if (installBasePart.directPartID) {
            return installBasePart.directPartID;
        } else {
            console.error('[getPartTypeID]: no part type ids');
        }
        return '';
    };

    /*
     * (reusable)
     * set the table filters to redux on each value change
     */
    const onValueChanges = (value: any, key: string) => {
        if (key === 'parts') {
            const foundWOPart = props.workOrderParts?.[value.value];

            setFormGroup({
                ...formGroup,
                value: {
                    parts: value,
                    quantity: foundWOPart ? foundWOPart.estimated : formGroup.value?.quantity
                }
            });
        } else if (key === 'quantity') {
            setFormGroup({
                ...formGroup,
                value: {
                    ...formGroup.value,
                    quantity: value
                }
            });
        } else {
            console.error('onValueChanges: key not found', key);
        }
    };

    const formControls: FieldConfig = React.useMemo(() => {
        const { partOptions, selectedParts } = props;

        const filteredPartOptions = partOptions.filter(
            option => !selectedParts.find(part => part.id === option.value || part.partStockID === option.value)
        );

        const defaultValues: IpartFormValues = selectedItemToFormValues(props.selectedInstallBasePartPopulated);
        const { isMobile } = props;
        const disabled = props.disabled ?? false;
        let saveButtonText = props.t('common:add');
        const { directPartQuantity, workOrderPartQuantity } = props.selectedInstallBasePartPopulated;

        const editing = props.selectedInstallBasePartPopulated.id.length > 0;
        if (editing) saveButtonText = props.t('update');

        let label = defaultValues.description;
        if (formGroup?.value?.parts?.label) {
            label = formGroup.value.parts.label;
        }

        let quantity = defaultValues.quantity ?? 1;

        if (
            directPartQuantity !== undefined ||
            workOrderPartQuantity !== undefined ||
            props.selectedInstallBasePartPopulated.partStockQuantity !== undefined
        ) {
            quantity =
                workOrderPartQuantity ||
                directPartQuantity ||
                props.selectedInstallBasePartPopulated.partStockQuantity ||
                1;
        }

        // MMG-1794 - For WorkOrder Parts, default the quantity to the estimated quantity
        if (formGroup?.value.parts?.value) {
            const part = Object.values(props.workOrderParts).filter(x => x.id === formGroup.value.parts.value);
            const firstPart = part[0];
            if (firstPart) {
                // There should only be 1 part here
                quantity = firstPart.estimated;
            }
        }

        const buttonStyle = { marginTop: '24px', width: isMobile ? '100%' : '' };
        let fieldConfigControls = {
            parts: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'manageWorkOrder:labelParts',
                    options: filteredPartOptions,
                    disableSort: true, // This lets us sort in the selector instead of complicating the sort logic
                    colWidthLarge: isMobile ? 12 : 6,
                    type: 'text',
                    placeholder: 'searchPlaceholder',
                    disabled: props.selectedInstallBasePartPopulated.id.length > 0 || props.disabled
                },
                options: {
                    validators: [FormUtil.validators.requiredWithTrim, Validators.min(1)]
                },
                formState: {
                    value: formGroup?.value?.parts?.value ? formGroup.value.parts.value : defaultValues.id,
                    label,
                    disabled: props.selectedInstallBasePartPopulated.id.length > 0
                }
            },
            quantity: {
                render: FormUtil.NumericInput,
                meta: {
                    label: 'quantity',
                    colWidth: isMobile ? 8 : 2,
                    name: 'quantity'
                },
                options: {
                    validators: [FormUtil.validators.requiredWithTrim, Validators.min(0)]
                },
                formState: { value: quantity.toString(), disabled }
            },
            $field_submit: {
                isStatic: false,
                render: () => (
                    <Col xs={isMobile ? 4 : 2} className="parts-btn_sm">
                        <Button
                            variant={props.colorButton || 'default'}
                            type="button"
                            onClick={handleSubmit}
                            style={buttonStyle}
                            disabled={disabled}
                        >
                            {saveButtonText}
                        </Button>
                    </Col>
                )
            },

            $field_cancel: {
                isStatic: false,
                render: () =>
                    editing ? (
                        <Col xs={isMobile ? 4 : 2} className="parts-btn">
                            <Button
                                type="button"
                                variant="default"
                                style={buttonStyle}
                                onClick={() => props.clearSelectedInstallBasePartID()}
                            >
                                {String(props.t('common:cancel'))}
                            </Button>
                        </Col>
                    ) : null
            }
        };

        return FormUtil.translateForm(
            {
                controls: { ...fieldConfigControls }
            },
            props.t
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formGroup?.value?.parts?.value,
        props.workOrderParts,
        props.selectedInstallBasePartPopulated.id.length,
        props.partOptions,
        props.selectedParts
    ]);

    React.useEffect(() => {
        if (!formRef) {
            return;
        }

        for (const key in formRef.controls) {
            formRef.get(key).valueChanges.subscribe((value: any) => {
                onValueChanges(value, key);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formGroup]);

    /**
     * if this is a new part prevent them from adding another installBasePart for the same partStock or jobPart
     * Only consider install base parts that are actually visible in the grid (not deleted)
     * @param installBasePart
     * @returns boolean
     */
    const getIsDuplicatePart = (installBasePart: IinstallBasePart): boolean => {
        const selectedInstallBasePartTypeID = getPartTypeID(installBasePart);

        // Filter tableParts to only consider parts that are actually visible in the grid
        // This excludes deleted install base parts and install base parts that reference deleted work order parts
        const visibleTableParts = props.tableParts.filter(iBP => {
            // Install base parts with isDeleted: true are already filtered out by the selector
            // but we need to also filter out install base parts that reference deleted work order parts
            if (iBP.workOrderPartID) {
                const workOrderPart = props.workOrderParts[iBP.workOrderPartID];
                // Only include if work order part exists and is not deleted
                return workOrderPart && workOrderPart.isDeleted === false;
            }
            // For non-work-order parts, include them
            return true;
        });

        const anyPartTypeID = visibleTableParts.map(iBP => getPartTypeID(iBP));

        if (installBasePart.id.length === 0 && includes(anyPartTypeID, selectedInstallBasePartTypeID)) {
            toastr.error(
                props.t('common:warning'),
                props.t('manageWorkOrder:identicalPartTypeID'),
                constants.toastrError
            );
            return true;
        }
        return false;
    };

    const handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (formRef.status === 'INVALID') {
            formRef.markAsSubmitted();
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            return;
        }
        const installBasePart = formValuesToSelectedItem();

        // For work order parts, you cannot use more than the estimated quantity
        if (installBasePart.workOrderPartID && installBasePart.workOrderPartQuantity) {
            const woPart = props.workOrderParts[installBasePart.workOrderPartID];
            if (woPart) {
                if (installBasePart.workOrderPartQuantity > woPart.estimated) {
                    toastr.error('Please enter a value less than the estimated quantity', '', constants.toastrError);
                    return;
                }
            }
        }

        if (getIsDuplicatePart(installBasePart)) {
            return;
        }

        props.saveInstallBasePart(installBasePart, props.selectedInstallBasePartPopulated);

        // If it's an update, clear after the save
        if (props.selectedInstallBasePartPopulated.id.length > 0) {
            props.clearSelectedInstallBasePartID();
        }

        setFormGroup((prev: FormGroup) => ({ ...prev, value: { ...prev.value, parts: undefined } }));
    };

    const setForm = (form: FormGroup | FormArray) => {
        formRef = form;
        formRef.meta = {
            loading: props.loading
        };
        formRef.value = formGroup?.value || initValue();

        setFormGroup({ ...formRef });
    };

    React.useEffect(() => {
        const { selectedInstallBasePartPopulated, partOptions } = props;
        const {
            id,
            part: { number, description },
            directPartQuantity,
            workOrderPartQuantity,
            partStockQuantity
        } = selectedInstallBasePartPopulated;

        if (formGroup && formGroup.setValue && selectedInstallBasePartPopulated?.part?.id) {
            const foundPart = partOptions.find(part => part.value === selectedInstallBasePartPopulated.id);
            const finalQuantity = directPartQuantity || workOrderPartQuantity || partStockQuantity || 1;
            setFormGroup((prev: FormGroup) => ({
                ...prev,
                value: {
                    parts: foundPart || { value: id, label: `${number} - ${description}` },
                    quantity: finalQuantity
                }
            }));
        } else {
            setFormGroup((prev: FormGroup) => ({
                ...prev,
                value: {
                    parts: undefined,
                    quantity: 1
                }
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedInstallBasePartPopulated, props.partOptions]);

    const { isMobile } = props;
    return (
        <FormSection>
            <form onSubmit={handleSubmit} className="clearfix beacon-form">
                <Row>
                    <FormGenerator onMount={setForm} fieldConfig={formControls} />
                </Row>
                {props.isUserUSA && !props.disabled && (
                    <Col xs={12} className="form-buttons">
                        <Button
                            variant="default"
                            style={{
                                width: isMobile ? '100%' : ''
                            }}
                            onClick={() => {
                                addQuery(suggestedPartsQueryParamsEnum.showSearchPartsModal, 'true', props.history);
                            }}
                        >
                            {String(props.t('manageWorkOrder:searchAllParts'))}
                        </Button>
                    </Col>
                )}
            </form>
        </FormSection>
    );
};

export default PartForm;
